import React, { useState, useEffect, useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import dayjs from 'dayjs';
import TopBarRedesigned from 'components/TopBarRedesigned/TopBarRedesigned';
import FixedLoader from 'components/FixedLoader';
import FixedError from 'components/FixedError';
import { useApi, useActionApi } from 'hooks';
import Api from 'api';
import { exitReact } from 'utils';
import { isShiftChatOpen } from 'utils/shiftChat';
import { getDentistShiftRoleLongLabel } from 'utils/dentist/shiftRoleLabel';
import { deliverInvoicePdf } from 'pages/hygienist/InvoicePage/invoicePdfDownload';
import ShiftDetailsTable from './components/ShiftDetailsTable';
import InvoiceSection from './components/InvoiceSection';
import SurveySection from './components/SurveySection';
import NotesSection from './components/NotesSection';
import {
  PageContainer,
  ShiftTypeHeader,
  ContentWrapper,
  LocationHeader,
  OfficeName,
  LocationInfo,
  MainCard,
  ProfessionalName,
  DoneButton,
  ChatNowSection,
  ChatNowButton,
} from './styles';
import { JobCompletedLocationState, JobCompletedData } from './types';
import {
  canEditDentistFeedback,
  canCompleteDentistSurvey,
  hasRealDentistSubmittedFeedback,
} from '../../../utils/feedbackEditEligibility';

// Access global TempStars object
const TempStars = (window as any).TempStars;

const normalizeFlag = (value?: boolean | number | string | null) =>
  value === true || value === 1 || value === '1';

const normalizeRating = (value?: number | string | null) => {
  const rating = Number(value);
  return Number.isFinite(rating) ? rating : 0;
};

/** Includes dismiss-only — used for queue / home prompts, not SurveySection CTA. */
export const computeHasDentistFeedback = (job: Partial<JobCompletedData>) => {
  const hasRating = normalizeRating(job.hygienistRating) > 0;
  return normalizeFlag(job.feedbackSubmitted) || normalizeFlag(job.surveyDismissed) || hasRating;
};

export const computeHasRealDentistSubmittedFeedback = (job: Partial<JobCompletedData>) =>
  hasRealDentistSubmittedFeedback(job);

/** @deprecated T2-6961 — edits removed; kept for test aliases during transition. */
export const computeCanEditDentistFeedback = (job: Partial<JobCompletedData>) =>
  canEditDentistFeedback(job);

/** Unsubmitted survey still fillable within the 30-day Toronto window (T2-6961). */
export const computeCanCompleteDentistSurvey = (job: Partial<JobCompletedData>) =>
  canCompleteDentistSurvey(job);

const JobCompletedPage: React.FC = () => {
  const history = useHistory();
  const location = useLocation<JobCompletedLocationState>();
  const { jobId, jobCount } = location.state || {};
  
  const [notes, setNotes] = useState('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [downloading, setDownloading] = useState<{ placement?: boolean; talent?: boolean }>({});
  const [downloadError, setDownloadError] = useState<string | null>(null);

  // Validate required state
  useEffect(() => {
    if (!jobId) {
      // JobCompletedPage: Missing required jobId
      history.push({ pathname: 'dentistHome' });
    }
  }, [jobId]);

  // Fetch job details
  const [jobData, isLoading, error, refetchJob] = useApi<void, JobCompletedData>({
    apiMethod: async () => {
      if (!jobId) throw new Error('Missing jobId');
      
      const [job, invoice] = await Promise.all([
        Api.getJobDetails(jobId),
        Api.getInvoiceForJob(jobId)
      ]);
      
      return {
        ...job,
        invoice,
        hasInvoice: !!invoice?.id,
        hasFeedback: computeHasDentistFeedback(job)
      } as JobCompletedData;
    },
    initialValue: {} as JobCompletedData,
    onSuccess: (data: JobCompletedData) => {
      setNotes(data.dentistPrivateNotes || '');
    }
  });

  // Fetch data on mount
  useEffect(() => {
    if (jobId) {
      refetchJob();
    }
  }, [jobId]); // Only depend on jobId, not refetchJob

  // Save notes action
  const [isSaving, saveError, saveNotes] = useActionApi<{ jobId: number; notes: string }, void>({
    apiMethod: Api.updateJobNotes,
    onSuccess: () => {
      setHasUnsavedChanges(false);
      // Navigate to React dentist home after successful save
      history.push({ pathname: 'dentistHome' });
    }
  });

  const handleBack = () => {
    if (hasUnsavedChanges) {
      // Could add confirmation dialog here
      if (!window.confirm('You have unsaved notes. Are you sure you want to leave?')) {
        return;
      }
    }
    history.push({ pathname: 'dentistHome' });
  };

  const handleChat = useCallback(() => {
    if (!jobData) return;
    history.push({
      pathname: 'messenger',
      state: {
        job: jobData,
        returnTo: { pathname: 'jobCompleted', state: { jobId: jobData.id, jobCount: 1 } },
      },
    });
  }, [history, jobData]);

  const handleViewInvoice = () => {
    if (!jobData) return;
    
    history.push({
      pathname: 'invoice',
      state: { 
        jobId: jobData.id, 
        job: jobData
      }
    });
  };

  const handleCompleteSurvey = async () => {
    // T2-6961: first-time Complete Survey only while within the 30-day window.
    if (!jobData || computeHasRealDentistSubmittedFeedback(jobData)) return;
    if (!computeCanCompleteDentistSurvey(jobData)) return;

    history.push({
      pathname: 'feedbackPage',
      state: {
        job: jobData,
        hygienist: jobData.hygienist,
        locationId: jobData.locationId,
        fromJobCompleted: true,
      },
    });
  };

  const handleViewFeedback = () => {
    if (!jobData) return;
    history.push({
      pathname: 'feedbackPage',
      state: {
        job: jobData,
        hygienist: jobData.hygienist,
        locationId: jobData.locationId,
        fromJobCompleted: true,
        viewOnly: true,
      },
    });
  };

  const handleNotesChange = (newNotes: string) => {
    setNotes(newNotes);
    setHasUnsavedChanges(newNotes !== (jobData?.dentistPrivateNotes || ''));
  };

  const handleDone = () => {
    if (hasUnsavedChanges && jobId) {
      saveNotes({ jobId, notes });
    } else {
      history.push({ pathname: 'dentistHome' });
    }
  };

  const getDateForFilename = () => {
    const raw = (jobData as any)?.startDate || jobData?.shifts?.[0]?.shiftDate;
    return raw ? dayjs(raw).format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD');
  };

  const triggerPdfDownload = (blob: Blob, filename: string) => {
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = filename;
    anchor.click();
    URL.revokeObjectURL(objectUrl);
  };

  const handleDownloadPlacementFee = async () => {
    if (!jobData?.id) return;
    setDownloadError(null);
    setDownloading((d) => ({ ...d, placement: true }));
    try {
      const blob = await Api.downloadPlacementFeeInvoice(jobData.id);
      triggerPdfDownload(blob, `placement-fee-invoice-${getDateForFilename()}.pdf`);
    } catch (e: any) {
      setDownloadError('Failed to download placement fee invoice.');
    } finally {
      setDownloading((d) => ({ ...d, placement: false }));
    }
  };

  const handleDownloadTalentInvoice = async () => {
    if (!jobData?.id) return;
    setDownloadError(null);
    setDownloading((d) => ({ ...d, talent: true }));
    try {
      const blob = await Api.downloadTalentInvoice(jobData.id);
      await deliverInvoicePdf(blob, `talent-invoice-${getDateForFilename()}.pdf`);
    } catch (e: any) {
      setDownloadError('Failed to download talent invoice.');
    } finally {
      setDownloading((d) => ({ ...d, talent: false }));
    }
  };

  if (isLoading) {
    return <FixedLoader loading={true} />;
  }

  if (error || !jobData?.id) {
    return (
      <PageContainer>
        <TopBarRedesigned
          title="Completed Shift"
          onBack={handleBack}
        />
        <FixedError error={error || new Error("Error loading job details")} />
      </PageContainer>
    );
  }

  const professionalType = getDentistShiftRoleLongLabel(jobData.type);
  const professionalName = `${jobData.hygienist.firstName} ${jobData.hygienist.lastName}`;
  const showPlacementFeeButton = Number(jobData?.dentistBilled) === 1 || Number(jobData?.dentistBilled) === 2;
  
  const showTalentInvoiceButton = !!jobData?.hasInvoice;
  const showChatNow = isShiftChatOpen(jobData);

  return (
    <PageContainer>
      <TopBarRedesigned
        title="Completed Shift"
        onBack={handleBack}
      />

      {/* Show loader overlay while preparing any PDF download or saving */}
      <FixedLoader loading={(!!downloading.placement) || (!!downloading.talent) || isSaving} />

      <ContentWrapper>
        <LocationHeader>
          <OfficeName>
            {jobData.location?.name || jobData.location?.practiceName || `Office ${jobData.location?.id || ''}`}
          </OfficeName>
          <LocationInfo>
            {jobData.location?.address}<br />
            {jobData.location?.city}, {jobData.location?.province}<br />
            {jobData.location?.postalCode}
          </LocationInfo>
        </LocationHeader>

        <MainCard>
          <ProfessionalName>{professionalName}</ProfessionalName>
          <ShiftDetailsTable 
            shifts={jobData.shifts} 
            hasInvoice={jobData.hasInvoice}
            shiftType={jobData.type}
            hourlyRate={jobData.hourlyRate}
          />
        </MainCard>

        <InvoiceSection
          hasInvoice={jobData.hasInvoice}
          invoice={jobData.invoice}
          paymentTerms={jobData.paymentTerms}
          onViewInvoice={handleViewInvoice}
        />

        {showChatNow && (
          <ChatNowSection>
            <ChatNowButton type="button" className="no-fastclick" onClick={handleChat}>
              Chat Now
            </ChatNowButton>
          </ChatNowSection>
        )}

        {/* Conditional PDF Download Buttons */}
        <div style={{ margin: '0 16px 16px' }}>
          {downloadError && (
            <div style={{ color: '#FB7672', marginBottom: 8 }}>{downloadError}</div>
          )}
          {showPlacementFeeButton && (
            <button
              onClick={handleDownloadPlacementFee}
              disabled={!!downloading.placement}
              style={{
                display: 'block',
                width: '100%',
                marginBottom: 8,
                backgroundColor: 'var(--primary-blue, #0B4598)',
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                padding: '12px 16px',
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              {downloading.placement ? 'Downloading Placement Fee Invoice…' : 'Download Placement Fee Invoice'}
            </button>
          )}
          {showTalentInvoiceButton && (
            <button
              onClick={handleDownloadTalentInvoice}
              disabled={!!downloading.talent}
              style={{
                display: 'block',
                width: '100%',
                backgroundColor: 'transparent',
                color: 'var(--primary-blue, #0B4598)',
                border: '1px solid var(--primary-blue, #0B4598)',
                borderRadius: 8,
                padding: '12px 16px',
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              {downloading.talent ? `Downloading ${professionalType} Invoice…` : `Download ${professionalType} Invoice`}
            </button>
          )}
        </div>

        <SurveySection
          hasFeedback={computeHasRealDentistSubmittedFeedback(jobData)}
          canCompleteSurvey={computeCanCompleteDentistSurvey(jobData)}
          onCompleteSurvey={handleCompleteSurvey}
          onViewFeedback={handleViewFeedback}
        />

        <NotesSection
          notes={notes}
          professionalName={jobData.hygienist.firstName}
          onNotesChange={handleNotesChange}
          onSave={handleDone}
          isSaving={isSaving}
        />

        <DoneButton 
          onClick={handleDone}
          disabled={isSaving}
        >
          {isSaving ? 'Saving...' : 'Done'}
        </DoneButton>
      </ContentWrapper>
    </PageContainer>
  );
};

export default JobCompletedPage;
